import { useState, useEffect, useRef } from 'react';
import type { AgentState, AgentLogEntry } from '../../hooks/useAgent';
import {
  X, Monitor, SkipBack, SkipForward, Play, Pause,
  CheckCircle2, ChevronUp, ChevronDown, Terminal, Edit3, Plug, Mail, Globe, Code2, Circle, Square
} from 'lucide-react';
import {
  buildComputerPanelModel,
  cleanComputerSnippet,
  cleanComputerText,
  isBrowserSemanticEvent,
  isNoisyComputerLine,
  type ComputerPanelMode,
} from '@/lib/computerPanelModel';

interface AgentSidePanelProps {
  state: AgentState;
  onClose: () => void;
  onFetchFile: (path: string) => Promise<string>;
  onBrowserTakeover?: () => void;
  onBrowserResume?: () => void;
  preferredTab?: PanelTab;
}

type PanelTab = ComputerPanelMode;

function repairPanelText(text: string): string {
  return cleanComputerText(text);
}

function isNoisyPanelLine(text: string): boolean {
  return isNoisyComputerLine(text);
}

function cleanPanelSnippet(text: string, fallback = 'Aktivitet registrert.'): string {
  return cleanComputerSnippet(text, fallback);
}

function semanticPanelText(event: AgentState['semanticEvents'][number] | null): string | null {
  if (!event) return null;
  const title = cleanComputerText(event.title);
  const text = cleanComputerText(event.text);
  if (title && text && !text.toLowerCase().startsWith(title.toLowerCase())) {
    return `${title}: ${text}`;
  }
  return text || title || null;
}

function laneLabel(lane?: string): string {
  const labels: Record<string, string> = {
    default: 'Generell agent',
    briefing: 'Briefing',
    report: 'Rapport',
    coordination: 'Koordinering',
    monitoring: 'Monitorering',
    incident: 'Incident',
  };
  return labels[lane ?? 'default'] ?? lane ?? 'Generell agent';
}

function capabilityLabel(name: string): string {
  const labels: Record<string, string> = {
    browser: 'Nettleser',
    web: 'Web',
    files: 'Filer',
    code: 'Kode',
    memory: 'Minne',
    gmail: 'Gmail',
    github: 'GitHub',
    calendar: 'Kalender',
    mcp: 'MCP',
    deploy: 'Deploy',
    sandbox: 'Sandbox',
    chat: 'Chat',
  };
  return labels[name] ?? name;
}

function capabilityIcon(name: string) {
  if (name === 'gmail') return <Mail size={12} style={{ color: '#EF4444' }} />;
  if (name === 'github') return <Code2 size={12} style={{ color: '#D1D5DB' }} />;
  if (name === 'mcp') return <Plug size={12} style={{ color: '#8B5CF6' }} />;
  if (name === 'browser' || name === 'web') return <Globe size={12} style={{ color: '#60A5FA' }} />;
  return <CheckCircle2 size={12} style={{ color: '#4ADE80' }} />;
}

function approvalLabel(approval: Record<string, unknown>): { title: string; text: string; meta: string } {
  const preview = approval.preview && typeof approval.preview === 'object'
    ? approval.preview as Record<string, unknown>
    : null;
  const tool = String(approval.tool ?? 'handling');
  const risk = String(approval.risk_level ?? approval.riskLevel ?? 'ukjent');
  const reason = String(approval.reason ?? 'approval');
  const description = String(approval.description ?? 'Godkjenning kreves.');
  const previewTitle = preview?.title ? String(preview.title) : null;
  const previewSummary = preview?.summary ? String(preview.summary) : null;
  return {
    title: previewTitle || tool,
    text: cleanPanelSnippet(previewSummary || description, 'Godkjenning kreves.'),
    meta: `Risiko: ${risk} · ${reason}`,
  };
}

// ─── Syntax highlight helper ──────────────────────────────────
function syntaxHighlight(code: string, ext: string): React.ReactNode[] {
  if (!['ts', 'tsx', 'js', 'jsx', 'py', 'json', 'css', 'html', 'sh', 'bash', 'md'].includes(ext)) {
    return [<span key="plain" style={{ color: '#D1D5DB' }}>{code}</span>];
  }

  const lines = code.split('\n');
  return lines.map((line, i) => {
    let color = '#D1D5DB';
    const trimmed = line.trim();

    // Markdown headings
    if (ext === 'md' && trimmed.startsWith('#')) {
      color = '#FCA5A5';
    }
    // Comments
    else if (trimmed.startsWith('//') || trimmed.startsWith('#') || trimmed.startsWith('*') || trimmed.startsWith('/*')) {
      color = '#6B7280';
    }
    // Keywords
    else if (/^(import|export|const|let|var|function|class|return|if|else|for|while|async|await|type|interface|from|default|extends|implements)\b/.test(trimmed)) {
      color = '#C084FC';
    }
    // Strings
    else if (/^['"`]/.test(trimmed) || (trimmed.includes(': "') || trimmed.includes(': \''))) {
      color = '#86EFAC';
    }
    // JSON keys
    else if (ext === 'json' && /^"[^"]+":/.test(trimmed)) {
      color = '#93C5FD';
    }
    // CSS properties
    else if (ext === 'css' && trimmed.includes(':')) {
      color = '#67E8F9';
    }
    return (
      <div key={i} style={{ color, lineHeight: '1.6', minHeight: '1.6em' }}>
        {line || '\u00A0'}
      </div>
    );
  });
}

// ─── Log line ─────────────────────────────────────────────────
function LogLine({ entry }: { entry: AgentLogEntry }) {
  const message = cleanPanelSnippet(entry.message, entry.type === 'error' ? 'Feil registrert.' : 'Aktivitet registrert.');
  const getColor = () => {
    switch (entry.type) {
      case 'tool_call': return '#93C5FD';
      case 'tool_result': return entry.success ? '#86EFAC' : '#F87171';
      case 'file_change': return '#C084FC';
      case 'error': return '#F87171';
      case 'thinking': return '#6B7280';
      default: return '#D1D5DB';
    }
  };

  const getPrefix = () => {
    switch (entry.type) {
      case 'tool_call': return '$ ';
      case 'tool_result': return entry.success ? '  ✓ ' : '  ✗ ';
      case 'file_change': return '  ~ ';
      case 'error': return '  ✗ ';
      default: return '  ';
    }
  };

  return (
    <div style={{
      display: 'flex',
      padding: '1px 0',
      fontSize: '12px',
      lineHeight: '1.65',
      fontFamily: '"SF Mono", "Fira Code", "Fira Mono", "Roboto Mono", Menlo, Monaco, Consolas, monospace',
    }}>
      <span style={{ color: getColor(), whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        <span style={{ opacity: 0.55 }}>{getPrefix()}</span>
        {message}
      </span>
    </div>
  );
}

// ─── File icon ────────────────────────────────────────────────
function getFileIcon(path: string): React.ReactNode {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const iconStyle: React.CSSProperties = {
    width: 14, height: 14, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0,
  };

  if (['ts', 'tsx'].includes(ext)) {
    return <span style={{ ...iconStyle, color: '#60A5FA', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>TS</span>;
  }
  if (['js', 'jsx'].includes(ext)) {
    return <span style={{ ...iconStyle, color: '#F59E0B', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>JS</span>;
  }
  if (ext === 'py') {
    return <span style={{ ...iconStyle, color: '#4ADE80', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>PY</span>;
  }
  if (ext === 'json') {
    return <span style={{ ...iconStyle, color: '#C084FC', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>{'{}'}</span>;
  }
  if (ext === 'md') {
    return <span style={{ ...iconStyle, color: '#94A3B8', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>MD</span>;
  }
  if (ext === 'css') {
    return <span style={{ ...iconStyle, color: '#F472B6', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>CS</span>;
  }
  if (['html', 'htm'].includes(ext)) {
    return <span style={{ ...iconStyle, color: '#FB923C', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>HT</span>;
  }
  if (['sh', 'bash'].includes(ext)) {
    return <Terminal size={12} style={{ color: '#86EFAC' }} />;
  }
  return <Edit3 size={12} style={{ color: '#9CA3AF' }} />;
}

// ─── Main panel ───────────────────────────────────────────────
export default function AgentSidePanel({ state, onClose, onFetchFile, onBrowserTakeover, onBrowserResume, preferredTab }: AgentSidePanelProps) {
  const initialModel = buildComputerPanelModel(state);
  const [activeTab, setActiveTab] = useState<PanelTab>(preferredTab ?? initialModel.mode);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [taskExpanded, setTaskExpanded] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const userSelectedTabRef = useRef(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const selectTab = (tab: PanelTab) => {
    userSelectedTabRef.current = true;
    setActiveTab(tab);
  };

  useEffect(() => {
    if (!preferredTab) return;
    const timer = window.setTimeout(() => {
      setActiveTab(current => (current === preferredTab ? current : preferredTab));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [preferredTab]);

  useEffect(() => {
    if (activeTab === 'terminal' && isPlaying) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state.logs, activeTab, isPlaying]);

  const handleFileClick = async (path: string) => {
    setSelectedFile(path);
    setLoadingFile(true);
    setActiveTab('editor');
    const content = await onFetchFile(path);
    setFileContent(content);
    setLoadingFile(false);
  };

  const isActive = ['planning', 'running'].includes(state.status);
  const model = buildComputerPanelModel(state, selectedFile);
  const runtimeFiles = model.runtimeFiles;
  const progressPct = model.progressPct;

  // Last tool call for the status bar
  const lastToolCall = [...state.logs].reverse().find(l => l.type === 'tool_call' || l.type === 'file_change');
  const lastFileChange = [...state.logs].reverse().find(l => l.type === 'file_change');
  const lastToolCallMessage = lastToolCall ? repairPanelText(lastToolCall.message) : '';
  const lastFileChangeMessage = lastFileChange ? repairPanelText(lastFileChange.message) : '';

  // File extension for syntax highlighting
  const selectedExt = selectedFile?.split('.').pop()?.toLowerCase() ?? '';
  const enabledCapabilities = Object.entries(state.capabilities ?? {}).filter(([, meta]) => meta.enabled);
  const unavailableCapabilities = Object.entries(state.capabilities ?? {}).filter(([, meta]) => !meta.enabled && meta.implemented);
  const intentDecision = state.intentDecision;
  const selectedCapabilities = state.selectedCapabilities ?? [];
  const preferredTools = state.preferredTools ?? [];
  const terminalLogs = state.logs.filter(entry => {
    if (isNoisyPanelLine(entry.message)) return false;
    const tool = entry.tool?.toLowerCase() ?? '';
    return entry.type === 'file_change'
      || tool.includes('terminal')
      || tool.includes('write_file')
      || tool.includes('read_file')
      || tool.includes('list_files')
      || tool.includes('python')
      || tool.includes('bash');
  });
  const liveBrowserFrame = state.browserFrame;
  const browserScreenshotSrc = liveBrowserFrame?.screenshot
    ? (liveBrowserFrame.screenshot.startsWith('data:')
      ? liveBrowserFrame.screenshot
      : `data:image/jpeg;base64,${liveBrowserFrame.screenshot}`)
    : null;
  const terminalFrames = state.terminalFrames.slice(-10);

  useEffect(() => {
    if (userSelectedTabRef.current) return;
    const timer = window.setTimeout(() => {
      setActiveTab(model.mode);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [model.mode]);

  useEffect(() => {
    if (activeTab !== 'editor' || selectedFile || !model.activeFile) return;
    let cancelled = false;
    void Promise.resolve().then(async () => {
      if (cancelled || !model.activeFile) return;
      setSelectedFile(model.activeFile);
      setLoadingFile(true);
      try {
        const content = await onFetchFile(model.activeFile);
        if (!cancelled) setFileContent(content);
      } finally {
        if (!cancelled) setLoadingFile(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeTab, model.activeFile, onFetchFile, selectedFile]);

  const visibleTabs: Array<{ id: PanelTab; label: string; icon: React.ReactNode; count?: number }> = [
    { id: 'browser', label: 'Browser', icon: <Globe size={14} /> },
    { id: 'editor', label: 'Editor', icon: <Edit3 size={14} />, count: runtimeFiles.length || undefined },
    { id: 'terminal', label: 'Terminal', icon: <Terminal size={14} /> },
    ...(model.hasAccessActivity ? [{ id: 'access' as const, label: 'Tilganger', icon: <Plug size={14} />, count: state.runtime.approvals.length || undefined }] : []),
  ];

  const terminalTitle = model.activeCommand ? model.activeCommand.slice(0, 72) : 'Shell idle';
  const showFileList = runtimeFiles.length > 1;
  const browserTakeoverActive = liveBrowserFrame?.status === 'takeover' || state.browserSession?.status === 'takeover_active';
  const browserNeedsTakeover = browserTakeoverActive || state.browserSession?.status === 'needs_takeover' || liveBrowserFrame?.status === 'takeover';
  const canTakeBrowserControl = activeTab === 'browser' && Boolean(liveBrowserFrame || state.browserSession);
  const semanticEvents = (state.semanticEvents ?? [])
    .filter(event => !isNoisyPanelLine(`${event.title ?? ''} ${event.text}`))
    .slice(-5);
  const latestSemanticEvent = semanticEvents.at(-1) ?? null;
  const browserSemanticEvents = semanticEvents.filter(isBrowserSemanticEvent).slice(-4);
  const latestBrowserSemanticEvent = browserSemanticEvents.at(-1) ?? null;
  const browserSemanticText = cleanPanelSnippet(
    liveBrowserFrame?.message
    || liveBrowserFrame?.action
    || semanticPanelText(latestBrowserSemanticEvent)
    || model.target,
    model.target,
  );
  const browserSemanticTitle = cleanPanelSnippet(
    liveBrowserFrame?.title
    || liveBrowserFrame?.url
    || latestBrowserSemanticEvent?.title
    || model.activeUrl
    || 'Nettleser aktiv',
    'Nettleser aktiv',
  );
  const latestSemanticText = cleanPanelSnippet(
    semanticPanelText(latestSemanticEvent) ?? model.target,
    model.target,
  );
  const summaryTitle = model.completionState === 'completed'
    ? 'Oppgave fullført'
    : model.completionState === 'failed'
      ? 'Kjøringen feilet'
      : model.completionState === 'stopped'
        ? 'Kjøringen er stoppet'
        : cleanPanelSnippet(latestSemanticEvent?.title ?? model.verb, 'Sine jobber');

  return (
    <div className="computer-panel">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="computer-panel-header">
        <div className="computer-panel-heading">
          <div className="computer-panel-title-row">
            <span className={`cp-live-dot ${model.completionState === 'live' || model.completionState === 'waiting' ? 'cp-live-dot--active' : ''}`} />
            <Monitor size={15} style={{ color: '#9A9A9A' }} />
            <span className="computer-panel-title">Sine's Computer</span>
          </div>
          <div className="computer-panel-subtitle">{model.statusLine}</div>
        </div>

        <div className="cp-icon-tabs" aria-label="Computer views">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              className={`cp-icon-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => selectTab(tab.id)}
              title={tab.label}
              aria-label={tab.label}
            >
              {tab.icon}
              {tab.count ? <span className="cp-icon-tab-badge">{tab.count}</span> : null}
            </button>
          ))}
        </div>

        <button className="computer-panel-close" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {/* ── Active file/command breadcrumb bar ─────────────── */}
      {lastToolCall && activeTab === 'terminal' && (
        <div className="computer-panel-status-bar">
          {lastFileChange ? (
            <>
              <Edit3 size={11} style={{ color: '#9CA3AF', flexShrink: 0 }} />
              <span className="computer-panel-status-text">
                {isActive ? 'Redigerer' : 'Redigerte'}
              </span>
              <span className="computer-panel-status-cmd">
                {cleanPanelSnippet(lastFileChangeMessage, 'Filaktivitet.')}
              </span>
            </>
          ) : (
            <>
              <Terminal size={11} style={{ color: '#9CA3AF', flexShrink: 0 }} />
              <span className="computer-panel-status-text">
                {isActive ? 'Kjører' : 'Kjørte'}
              </span>
              <span className="computer-panel-status-cmd">
                {cleanPanelSnippet(lastToolCallMessage, 'Kjører verktøy.')}
              </span>
            </>
          )}
        </div>
      )}

      {/* ── Body ───────────────────────────────────────────── */}
      <div className="computer-panel-body">

        {/* Browser tab */}
        {activeTab === 'browser' && (
          <div className="computer-browser-only">
            {browserScreenshotSrc ? (
              <div className="computer-browser-viewport-shell">
                <div className="computer-browser-viewport-title">
                  {cleanPanelSnippet(liveBrowserFrame?.title || liveBrowserFrame?.url, 'Live browser')}
                </div>
                <img
                  src={browserScreenshotSrc}
                  alt="Live nettleser"
                  className="computer-browser-only__image"
                />
                {canTakeBrowserControl && (
                  <div className={`computer-browser-takeover ${browserNeedsTakeover ? 'is-needed' : ''}`}>
                    <button
                      type="button"
                      className="computer-browser-jump-live"
                      disabled={!browserTakeoverActive}
                      title={browserTakeoverActive ? 'Se live nettleser' : 'Du er allerede på live-visningen'}
                    >
                      <Play size={16} />
                      Jump to live
                    </button>
                    {browserTakeoverActive ? (
                      <button
                        type="button"
                        className="computer-browser-take-control active"
                        onClick={onBrowserResume}
                        title="Gi kontrollen tilbake til Sine"
                      >
                        <CheckCircle2 size={18} />
                        Ferdig, fortsett
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="computer-browser-take-control"
                        onClick={onBrowserTakeover}
                        title="Ta kontroll over nettleseren"
                      >
                        <Monitor size={18} />
                        Take control
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : liveBrowserFrame || latestBrowserSemanticEvent ? (
              <div className="computer-browser-only__status">
                <div className="computer-browser-only__status-dot" />
                <div className="computer-browser-only__status-copy">
                  <div className="computer-browser-only__status-title">
                    {repairPanelText(browserSemanticTitle)}
                  </div>
                  {(liveBrowserFrame?.url || model.activeUrl) && (
                    <div className="computer-browser-only__status-url">
                      {repairPanelText(liveBrowserFrame?.url || model.activeUrl || '')}
                    </div>
                  )}
                  <div className="computer-browser-only__status-message">
                    {repairPanelText(browserSemanticText
                      || (liveBrowserFrame?.status === 'error'
                        ? 'Nettleseren støtte på en feil, men Sine fortsetter med en tryggere vei.'
                        : 'Live browser er aktiv. Skjermbildet er ikke tilgjengelig akkurat nå.')
                    )}
                  </div>
                  {browserSemanticEvents.length > 1 && (
                    <div className="computer-browser-semantic-events">
                      {browserSemanticEvents.map(event => (
                        <div key={event.id} className={`computer-browser-semantic-event ${event.status ?? 'info'}`}>
                          <span />
                          <p>{cleanPanelSnippet(semanticPanelText(event), 'Browser-aktivitet registrert.')}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {canTakeBrowserControl && (
                    <div className="computer-browser-status-actions">
                      {browserTakeoverActive ? (
                        <button type="button" className="computer-browser-take-control active" onClick={onBrowserResume}>
                          <CheckCircle2 size={16} />
                          Ferdig, fortsett
                        </button>
                      ) : (
                        <button type="button" className="computer-browser-take-control" onClick={onBrowserTakeover}>
                          <Monitor size={16} />
                          Take control
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="computer-browser-only__empty" style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#5F6673',
                fontSize: 12,
              }}>
                Nettleseren starter når Sine trenger den.
              </div>
            )}
          </div>
        )}

        {/* Terminal tab */}
        {activeTab === 'terminal' && (
          <div className="computer-terminal">
            <div className="computer-terminal-title">
              <Terminal size={11} style={{ color: '#6B7280' }} />
              <span>{terminalTitle}</span>
            </div>

            <div className="computer-terminal-content">
              {/* Prompt */}
              <div style={{
                fontFamily: '"SF Mono", "Fira Code", Menlo, monospace',
                fontSize: 12,
                marginBottom: 6,
                display: 'flex',
                gap: 0,
              }}>
                <span style={{ color: '#86EFAC' }}>ubuntu@sandbox</span>
                <span style={{ color: '#6B7280' }}>:</span>
                <span style={{ color: '#93C5FD' }}>~/sine</span>
                <span style={{ color: '#D1D5DB' }}> $</span>
              </div>

              {terminalFrames.length === 0 && terminalLogs.length === 0 ? (
                <div className="computer-empty-state cp-center-empty">
                  <Terminal size={14} />
                  <span>{model.completionState === 'completed' ? 'Oppgave fullført. Ingen kommando kjører.' : 'Ingen kommando kjører akkurat nå.'}</span>
                </div>
              ) : (
                <>
                  {terminalFrames.map(frame => (
                    <div key={frame.id} style={{ marginBottom: 10 }}>
                      <div style={{
                        color: frame.status === 'running' ? '#93C5FD' : frame.status === 'error' ? '#F87171' : '#86EFAC',
                        fontSize: 12,
                        lineHeight: 1.55,
                        fontFamily: '"SF Mono", "Fira Code", Menlo, monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        <span style={{ opacity: 0.55 }}>$ </span>{frame.command || 'kommando'}
                      </div>
                      {(frame.output || frame.error) && (
                        <pre style={{
                          margin: '4px 0 0',
                          padding: '7px 8px',
                          borderRadius: 7,
                          background: '#101820',
                          border: '1px solid #1F2937',
                          color: frame.error ? '#FCA5A5' : '#CBD5E1',
                          fontSize: 11,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: '"SF Mono", "Fira Code", Menlo, monospace',
                        }}>
                          {repairPanelText(frame.error || frame.output || '').slice(0, 3000)}
                        </pre>
                      )}
                    </div>
                  ))}
                  {terminalLogs.slice(-8).map(entry => <LogLine key={entry.id} entry={entry} />)}
                </>
              )}

              {/* Blinking cursor */}
              {isActive && (
                <div style={{
                  display: 'inline-block',
                  width: 7,
                  height: 14,
                  background: '#D1D5DB',
                  marginTop: 3,
                  animation: 'blink 1s step-end infinite',
                  verticalAlign: 'middle',
                }} />
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}

        {/* Files tab */}
        {activeTab === 'editor' && (
          <div className="computer-editor-layout">

            {/* File tree sidebar */}
            {showFileList && <div className="computer-editor-file-list">
              {runtimeFiles.length === 0 ? (
                <div className="computer-files-empty">
                  Ingen filer i denne kjøringen.
                </div>
              ) : (
                runtimeFiles.map(file => {
                  const fname = file.path.split('/').pop() ?? file.path;
                  const fdir = file.path.includes('/') ? file.path.split('/').slice(0, -1).join('/') : '';
                  const isSelected = selectedFile === file.path;
                  return (
                    <div
                      key={file.path}
                      onClick={() => handleFileClick(file.path)}
                      className={`computer-file-row${isSelected ? ' active' : ''}`}
                    >
                      {getFileIcon(file.path)}
                      <div className="computer-file-meta">
                        <div className="computer-file-name">
                          {fname}
                        </div>
                        {fdir && (
                          <div className="computer-file-dir">
                            {fdir}
                          </div>
                        )}
                      </div>
                      <span className="computer-file-badge">
                        {file.action === 'created' ? 'NY' : 'MOD'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>}

            {/* File content viewer */}
            {selectedFile ? (
              <div className="computer-editor-shell">
                {/* File title bar */}
                <div className="computer-editor-titlebar">
                  {getFileIcon(selectedFile)}
                  <span className="computer-editor-title">
                    {selectedFile.split('/').pop()}
                  </span>
                  <button
                    onClick={() => setSelectedFile(null)}
                    style={{
                      background: 'transparent', border: 'none', color: '#4B5563',
                      cursor: 'pointer', fontSize: 14, padding: '0 2px',
                      lineHeight: 1, display: 'flex', alignItems: 'center',
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Code content */}
                <div className="computer-editor-content">
                  {loadingFile ? (
                    <div className="computer-empty-state">
                      <Edit3 size={14} />
                      <span>Reading file {selectedFile}...</span>
                    </div>
                  ) : (
                    <div className="computer-code-block computer-code-block--editor">
                      {syntaxHighlight(fileContent, selectedExt)}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="computer-editor-empty">
                <Edit3 size={18} />
                <div>
                  <div className="computer-editor-empty-title">
                    {model.completionState === 'completed' ? 'Ingen filer ble åpnet i denne kjøringen.' : 'Editor åpnes når Sine leser eller endrer filer.'}
                  </div>
                  <div className="computer-editor-empty-copy">
                    Siste aktivitet: {model.target}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'summary' && (
          <div className="computer-summary">
            <div className={`computer-summary-icon ${model.completionState}`}>
              {model.completionState === 'completed' ? <CheckCircle2 size={28} /> : model.completionState === 'failed' ? <X size={28} /> : <Monitor size={28} />}
            </div>
            <div className="computer-summary-title">{summaryTitle}</div>
            <div className="computer-summary-copy">{latestSemanticText}</div>
            {semanticEvents.length > 0 && (
              <div className="computer-summary-events">
                {semanticEvents.map(event => (
                  <div key={event.id} className={`computer-summary-event ${event.status ?? 'info'}`}>
                    <span className="computer-summary-event-dot" />
                    <div>
                      <strong>{cleanPanelSnippet(event.title ?? event.kind, event.kind)}</strong>
                      <p>{cleanPanelSnippet(event.text, 'Aktivitet registrert.')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'access' && (
          <div style={{ padding: '14px 14px 18px', overflowY: 'auto' }}>
            {intentDecision && (
              <div style={{
                marginBottom: 14,
                padding: '12px 12px 10px',
                borderRadius: 10,
                border: '1px solid #1F1F1F',
                background: '#111111',
              }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Valgt lane
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    fontSize: 11,
                    color: '#E5E5E5',
                    background: 'rgba(96,165,250,0.12)',
                    border: '1px solid rgba(96,165,250,0.2)',
                    borderRadius: 999,
                    padding: '4px 9px',
                    fontWeight: 600,
                  }}>
                    {laneLabel(intentDecision.lane)}
                  </span>
                  <span style={{ fontSize: 11, color: '#6B7280' }}>
                    response: {intentDecision.response_mode}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#B8B8B8', lineHeight: 1.6 }}>
                  {intentDecision.rationale}
                </div>
              </div>
            )}

            {selectedCapabilities.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Prioritert for denne oppgaven
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedCapabilities.map(capability => (
                    <div key={capability.name} style={{
                      padding: '10px 12px',
                      background: '#111111',
                      border: '1px solid #1F1F1F',
                      borderRadius: 10,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                        {capabilityIcon(capability.name)}
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#E5E5E5' }}>
                          {capabilityLabel(capability.name)}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: '#B8B8B8', lineHeight: 1.55, marginBottom: 6 }}>
                        {capability.reason}
                      </div>
                      <div style={{ fontSize: 11, color: '#7C7C7C', lineHeight: 1.5 }}>
                        {(capability.tools ?? []).length > 0 ? capability.tools.join(', ') : 'Ingen direkte tools'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preferredTools.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Bør prøves først
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {preferredTools.map(tool => (
                    <span key={tool} style={{
                      fontSize: 11,
                      color: '#D1D5DB',
                      border: '1px solid rgba(96,165,250,0.18)',
                      background: 'rgba(96,165,250,0.08)',
                      borderRadius: 999,
                      padding: '4px 9px',
                    }}>
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {state.unavailableConnectors && state.unavailableConnectors.length > 0 && (
              <div style={{
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(245,158,11,0.28)',
                background: 'rgba(245,158,11,0.08)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B', marginBottom: 6 }}>
                  Valgte connectors som ikke er tilgjengelige
                </div>
                <div style={{ fontSize: 12, color: '#C5C5C5', lineHeight: 1.6 }}>
                  {state.unavailableConnectors.join(', ')}
                </div>
              </div>
            )}

            {state.pendingApproval && (
              <div style={{
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(249,115,22,0.28)',
                background: 'rgba(249,115,22,0.08)',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#FB923C', marginBottom: 6 }}>
                  Venter på godkjenning
                </div>
                <div style={{ fontSize: 12, color: '#C5C5C5', lineHeight: 1.6 }}>
                  {state.pendingApproval.description}
                </div>
              </div>
            )}

            {state.runtime.approvals.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Godkjenninger
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {state.runtime.approvals.slice(-5).map((approval, index) => {
                    const approvalView = approvalLabel(approval);
                    return (
                    <div key={`runtime-approval-${index}`} style={{
                      padding: '10px 12px',
                      background: 'rgba(249,115,22,0.08)',
                      border: '1px solid rgba(249,115,22,0.22)',
                      borderRadius: 10,
                      color: '#E5E5E5',
                      fontSize: 12,
                      lineHeight: 1.55,
                    }}>
                      <div style={{ fontWeight: 650, color: '#FDBA74', marginBottom: 4 }}>{approvalView.title}</div>
                      <div style={{ color: '#E5E7EB', marginBottom: 6 }}>{approvalView.text}</div>
                      <div style={{ color: '#9CA3AF', fontSize: 11 }}>{approvalView.meta}</div>
                    </div>
                  )})}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                Tilgjengelig i denne kjøringen
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {enabledCapabilities.map(([name, meta]) => (
                  <div key={name} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 12px',
                    background: '#111111',
                    border: '1px solid #1F1F1F',
                    borderRadius: 10,
                  }}>
                    <div style={{ marginTop: 1 }}>{capabilityIcon(name)}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#E5E5E5', marginBottom: 4 }}>
                        {capabilityLabel(name)}
                      </div>
                      <div style={{ fontSize: 11, color: '#8A8A8A', lineHeight: 1.5 }}>
                        {(meta.tools ?? []).length > 0 ? meta.tools.join(', ') : 'Ingen direkte tools'}
                      </div>
                      {name === 'mcp' && meta.servers && meta.servers.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {meta.servers.map(server => (
                            <span key={server.id} style={{
                              fontSize: 11,
                              color: '#D1D5DB',
                              background: 'rgba(139,92,246,0.12)',
                              border: '1px solid rgba(139,92,246,0.18)',
                              borderRadius: 999,
                              padding: '3px 8px',
                            }}>
                              {server.name}
                              {server.connector_name ? ` · ${server.connector_name}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {unavailableCapabilities.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Ikke tilgjengelig ennå
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {unavailableCapabilities.map(([name]) => (
                    <span key={name} style={{
                      fontSize: 11,
                      color: '#6B7280',
                      border: '1px solid #242424',
                      background: '#101010',
                      borderRadius: 999,
                      padding: '4px 9px',
                    }}>
                      {capabilityLabel(name)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {enabledCapabilities.length === 0 && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                Ingen runtime-tilganger registrert ennå for denne kjøringen.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Playback bar ───────────────────────────────────── */}
      <div className="computer-playback-bar">
        <button
          className="computer-playback-btn disabled"
          title="Tilbake"
          disabled
          onClick={() => {}}
        >
          <SkipBack size={12} />
        </button>
        <button
          className="computer-playback-btn"
          title={isPlaying ? 'Pause' : 'Spill av'}
          onClick={() => setIsPlaying(p => !p)}
        >
          {isPlaying ? <Pause size={12} /> : <Play size={12} />}
        </button>
        <button
          className="computer-playback-btn disabled"
          title="Frem"
          disabled
          onClick={() => {}}
        >
          <SkipForward size={12} />
        </button>

        {/* Progress track */}
        <div className="computer-playback-track">
          <div
            className="computer-playback-fill"
            style={{ width: `${progressPct}%` }}
          />
          <div
            className="computer-playback-dot"
            style={{ left: `calc(${progressPct}% - 5px)` }}
          />
        </div>

        {/* Live / done indicator */}
        <div className="computer-live-badge">
          {(model.completionState === 'live' || model.completionState === 'waiting') && <span className="computer-live-dot" />}
          <span style={{ fontSize: 11, color: isActive ? '#E5E5E5' : '#6B7280' }}>
            {model.completionState === 'live' ? 'live' : model.completionState === 'waiting' ? 'venter' : model.completionState === 'completed' ? 'ferdig' : model.completionState}
          </span>
        </div>
      </div>

      {/* ── Task footer ────────────────────────────────────── */}
      <div
        className="computer-task-footer"
        onClick={() => setTaskExpanded(v => !v)}
      >
        {model.completionState === 'live'
          ? <Circle size={12} style={{ color: '#60A5FA', fill: '#60A5FA', flexShrink: 0 }} />
          : model.completionState === 'completed'
            ? <CheckCircle2 size={13} style={{ color: '#4ADE80', flexShrink: 0 }} />
            : <Square size={12} style={{ color: '#F59E0B', flexShrink: 0 }} />
        }
        <span className="computer-task-footer-label">
          {model.taskLabel}
        </span>
        {model.taskCounter && <span className="computer-task-footer-counter">{model.taskCounter}</span>}
        {taskExpanded
          ? <ChevronUp size={12} style={{ color: '#6B7280', flexShrink: 0 }} />
          : <ChevronDown size={12} style={{ color: '#6B7280', flexShrink: 0 }} />
        }
      </div>
    </div>
  );
}
